import { Document, Page, Text, View, StyleSheet, Image, pdf, Font } from '@react-pdf/renderer';
import templateImage from '../assets/img/template.png';

// Enregistrement des polices pour supporter les caractères français
Font.register({
  family: 'Roboto',
  fonts: [
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-light-webfont.ttf',
      fontWeight: 300,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-regular-webfont.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-medium-webfont.ttf',
      fontWeight: 500,
    },
    {
      src: 'https://cdnjs.cloudflare.com/ajax/libs/ink/3.1.10/fonts/Roboto/roboto-bold-webfont.ttf',
      fontWeight: 700,
    },
  ],
});

// Styles pour le PDF
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    fontFamily: 'Roboto',
  },
  backgroundContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: -10,
  },
  templateImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    opacity: 1,
  },
  content: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
  header: {
    position: 'absolute',
    top: 180,
    left: 60,
    right: 60,
    textAlign: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    marginBottom: 8,
    color: '#000000',
  },
  section: {
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6,
    color: '#2B6CB0',
  },
  text: {
    fontSize: 9.5,
    marginBottom: 3,
    lineHeight: 1.4,
    color: '#000000',
  },
  row: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  label: {
    fontSize: 9.5,
    fontWeight: 700,
    width: '35%',
    color: '#000000',
  },
  value: {
    fontSize: 9.5,
    width: '65%',
    color: '#000000',
  },
  contentWrapper: {
    position: 'absolute',
    top: 240,
    left: 60,
    right: 60,
    bottom: 150,
  },
  twoColumnRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  column: {
    width: '48%',
  },
  fullWidth: {
    width: '100%',
    marginBottom: 12,
  },
  signature: {
    position: 'absolute',
    bottom: 120,
    right: 60,
    width: 120,
    height: 60,
  },
  footer: {
    position: 'absolute',
    bottom: 30,
    left: 60,
    right: 60,
    fontSize: 7,
    textAlign: 'center',
    color: '#666',
  },
});

// Composant Document PDF
const RapportMedicalDocument = ({ rapport, mission, patient, medecin, signatureUrl }) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* Template en arrière-plan - PLACÉ EN PREMIER */}
      
      
      {/* Contenu du rapport - PLACÉ EN SECOND */}
      <View style={styles.content}>
        {/* En-tête */}
        <View style={styles.header}>
          <Text style={styles.title}>COMPTE RENDU MÉDICAL</Text>
          <Text style={{ fontSize: 9 }}>
            Rapport N° {rapport.id} - {new Date(rapport.cree_le || Date.now()).toLocaleDateString('fr-FR')}
          </Text>
        </View>

        {/* Conteneur principal avec positionnement absolu */}
        <View style={styles.contentWrapper}>
        
        {/* Ligne 1: Informations Patient (gauche) + Informations Mission (droite) */}
        <View style={styles.twoColumnRow}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>INFORMATIONS PATIENT</Text>
            {patient?.nom_complet && (
              <View style={styles.row}>
                <Text style={styles.label}>Nom complet :</Text>
                <Text style={styles.value}>{patient.nom_complet}</Text>
              </View>
            )}
            {(rapport.age || patient?.age) && (
              <View style={styles.row}>
                <Text style={styles.label}>Âge :</Text>
                <Text style={styles.value}>{rapport.age || patient.age} ans</Text>
              </View>
            )}
            {patient?.sexe && (
              <View style={styles.row}>
                <Text style={styles.label}>Sexe :</Text>
                <Text style={styles.value}>{patient.sexe}</Text>
              </View>
            )}
            {patient?.téléphone && (
              <View style={styles.row}>
                <Text style={styles.label}>Téléphone :</Text>
                <Text style={styles.value}>{patient.téléphone}</Text>
              </View>
            )}
          </View>

          <View style={styles.column}>
            <Text style={styles.sectionTitle}>INFORMATIONS MISSION</Text>
            {rapport.mission_id && (
              <View style={styles.row}>
                <Text style={styles.label}>Mission N° :</Text>
                <Text style={styles.value}>{rapport.mission_id}</Text>
              </View>
            )}
            {mission?.date_mission && (
              <View style={styles.row}>
                <Text style={styles.label}>Date :</Text>
                <Text style={styles.value}>
                  {new Date(mission.date_mission).toLocaleDateString('fr-FR')}
                </Text>
              </View>
            )}
            {(mission?.adresse || mission?.lieu) && (
              <View style={styles.row}>
                <Text style={styles.label}>Lieu :</Text>
                <Text style={styles.value}>{mission.adresse || mission.lieu}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Ligne 2: Motif (gauche) + Antécédents (droite) */}
        {(rapport.motif || rapport.antecedent) && (
          <View style={styles.twoColumnRow}>
            {rapport.motif && (
              <View style={styles.column}>
                <Text style={styles.sectionTitle}>MOTIF DE L'INTERVENTION</Text>
                <Text style={styles.text}>{rapport.motif}</Text>
              </View>
            )}

            {rapport.antecedent && (
              <View style={styles.column}>
                <Text style={styles.sectionTitle}>ANTÉCÉDENTS</Text>
                <Text style={styles.text}>{rapport.antecedent}</Text>
              </View>
            )}
          </View>
        )}

        {/* Histoire de la maladie - Pleine largeur */}
        {rapport.HDM && (
          <View style={styles.fullWidth}>
            <Text style={styles.sectionTitle}>HISTOIRE DE LA MALADIE</Text>
            <Text style={styles.text}>{rapport.HDM}</Text>
          </View>
        )}

        {/* Ligne 3: Constantes vitales (gauche) + Actes réalisés (droite) */}
        {((rapport.ta || rapport.fc || rapport.fr || rapport.spo2 || rapport.t || rapport.dextro || rapport.GCS) || 
          (rapport.VVP || rapport.solutes || rapport.medicaments || rapport.medicaments_list || rapport.o2)) && (
          <View style={styles.twoColumnRow}>
            {(rapport.ta || rapport.fc || rapport.fr || rapport.spo2 || rapport.t || rapport.dextro || rapport.GCS) && (
              <View style={styles.column}>
                <Text style={styles.sectionTitle}>CONSTANTES VITALES</Text>
                {rapport.ta && (
                  <View style={styles.row}>
                    <Text style={styles.label}>TA :</Text>
                    <Text style={styles.value}>{rapport.ta} mmHg</Text>
                  </View>
                )}
                {rapport.fc && (
                  <View style={styles.row}>
                    <Text style={styles.label}>FC :</Text>
                    <Text style={styles.value}>{rapport.fc} bpm</Text>
                  </View>
                )}
                {rapport.fr && (
                  <View style={styles.row}>
                    <Text style={styles.label}>FR :</Text>
                    <Text style={styles.value}>{rapport.fr} /min</Text>
                  </View>
                )}
                {rapport.spo2 && (
                  <View style={styles.row}>
                    <Text style={styles.label}>SpO2 :</Text>
                    <Text style={styles.value}>{rapport.spo2} %</Text>
                  </View>
                )}
                {rapport.t && (
                  <View style={styles.row}>
                    <Text style={styles.label}>Température :</Text>
                    <Text style={styles.value}>{rapport.t} °C</Text>
                  </View>
                )}
                {rapport.dextro && (
                  <View style={styles.row}>
                    <Text style={styles.label}>Dextro :</Text>
                    <Text style={styles.value}>{rapport.dextro} g/L</Text>
                  </View>
                )}
                {rapport.GCS && (
                  <View style={styles.row}>
                    <Text style={styles.label}>GCS :</Text>
                    <Text style={styles.value}>{rapport.GCS}/15</Text>
                  </View>
                )}
              </View>
            )}

            {(rapport.VVP || rapport.solutes || rapport.medicaments || rapport.medicaments_list || rapport.o2) && (
              <View style={styles.column}>
                <Text style={styles.sectionTitle}>ACTES RÉALISÉS</Text>
                {(rapport.VVP || rapport.solutes || rapport.medicaments) && (
                  <Text style={styles.text}>
                    {rapport.VVP ? '✓ VVP' : ''}
                    {rapport.VVP && (rapport.solutes || rapport.medicaments) ? ' | ' : ''}
                    {rapport.solutes ? '✓ Solutés' : ''}
                    {rapport.solutes && rapport.medicaments ? ' | ' : ''}
                    {rapport.medicaments ? '✓ Médicaments' : ''}
                  </Text>
                )}
                {rapport.medicaments_list && (
                  <Text style={styles.text}>Liste : {rapport.medicaments_list}</Text>
                )}
                {rapport.o2 && (
                  <Text style={styles.text}>O2 : {rapport.o2} L/min</Text>
                )}
              </View>
            )}
          </View>
        )}

        {/* Ligne 4: Synthèse (gauche) + Conduite à tenir (droite) */}
        {(rapport.description || rapport.ordonnance || rapport.transport || rapport.bilan) && (
          <View style={styles.twoColumnRow}>
            {rapport.description && (
              <View style={styles.column}>
                <Text style={styles.sectionTitle}>SYNTHÈSE</Text>
                <Text style={styles.text}>{rapport.description}</Text>
              </View>
            )}

            {(rapport.ordonnance || rapport.transport || rapport.bilan) && (
              <View style={styles.column}>
                <Text style={styles.sectionTitle}>CONDUITE À TENIR</Text>
                <Text style={styles.text}>
                  {rapport.ordonnance ? '✓ Ordonnance établie' : ''}
                  {rapport.ordonnance && rapport.transport ? ' | ' : ''}
                  {rapport.transport ? '✓ Transport vers hôpital' : ''}
                  {rapport.bilan ? ' | ✓ Bilan réalisé' : ''}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Médecin */}
        <View style={{ marginTop: 20 }}>
          <Text style={styles.text}>
            Médecin : Dr. {medecin?.prenom || rapport.medecin_prenom || ''} {medecin?.nom || rapport.medecin_nom || ''}
          </Text>
          <Text style={styles.text}>
            Date : {new Date(rapport.cree_le || Date.now()).toLocaleDateString('fr-FR')} à{' '}
            {new Date(rapport.cree_le || Date.now()).toLocaleTimeString('fr-FR', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </Text>
        </View>
        </View>

        {/* Signature */}
        {signatureUrl && (
          <Image src={signatureUrl} style={styles.signature} />
        )}

        {/* Footer */}
        <Text style={styles.footer}>
          Document généré par OmniDoc - Système de Gestion SAMU
        </Text>
        <View style={styles.backgroundContainer}>
        <Image src={templateImage} style={styles.templateImage} />
      </View>
      </View>
    </Page>
  </Document>
);

/**
 * Génère et télécharge le PDF du rapport médical
 * @param {Object} rapport - Données du rapport médical
 * @param {Object} mission - Données de la mission (optionnel)
 * @param {Object} patient - Données du patient (optionnel)
 * @param {Object} medecin - Données du médecin (optionnel)
 * @param {string} signatureUrl - URL ou data URI de la signature (optionnel)
 */
export const generateRapportMedicalPDF = async (rapport, mission = null, patient = null, medecin = null, signatureUrl = null) => {
  try {
    // Créer le document PDF
    const blob = await pdf(
      <RapportMedicalDocument 
        rapport={rapport}
        mission={mission}
        patient={patient}
        medecin={medecin}
        signatureUrl={signatureUrl}
      />
    ).toBlob();

    // Télécharger le fichier
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `rapport-medical-${rapport.id}-${new Date().getTime()}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    return { success: false, error: error.message };
  }
};

export default { generateRapportMedicalPDF };